import { Trans, useTranslation } from "react-i18next";
import { X } from "lucide-react";
import type { ComponentType } from "react";
import FilterCard from "../../Cards/FilterCard/FilterCard";
import { defaultFilters, filterTitles } from "../../../constants/filters";
import { formatSelectedFilters, deeplyEqual } from "../../../utils/filters";
import { FilterDataProps, HandleFilterType } from "../../../types/filter.types";
import { FilterTypes, ProductSectionProps, ProductTypes } from "../../../types/product.types";
import { FilterCardConfig } from "../../../constants/landingSections";

type CatalogData = {
    productItems: ProductTypes[];
    newProductItems: ProductTypes[];
    packageItems: ProductTypes[];
    extraItems: ProductTypes[];
    activityItems: ProductTypes[];
    filterDict: Record<string, ProductTypes[]>;
};

type LandingOffersSectionProps = {
    styles: Record<string, string>;
    filterCards: FilterCardConfig[];
    filterData: FilterDataProps;
    handleFilter: HandleFilterType;
    basicFilter?: FilterTypes;
    handleBasicFilter?: (value: FilterTypes) => void;
    productLayout: "list" | "block";
    setProductLayout: (value: string) => void;
    onOpenFilter: () => void;
    onOpenTimeModal: () => void;
    catalogData: CatalogData;
    ProductSectionComponent: ComponentType<ProductSectionProps>;
    loading: boolean;
    loadingActivities?: boolean;
    showSalesButtons?: boolean;
    titleId?: string;
    formatPriceRange: (min: number, max: number) => string;
};

const LandingOffersSection = ({
    styles,
    filterCards,
    filterData,
    handleFilter,
    basicFilter,
    handleBasicFilter,
    productLayout,
    setProductLayout,
    onOpenFilter,
    onOpenTimeModal,
    catalogData,
    ProductSectionComponent,
    loading,
    loadingActivities,
    showSalesButtons = true,
    titleId,
    formatPriceRange,
}: LandingOffersSectionProps) => {
    const { t } = useTranslation();
    const handleBasic = handleBasicFilter ?? (() => {});
    const { productItems, newProductItems, packageItems, extraItems, activityItems, filterDict } = catalogData;
    const selectedFilter = basicFilter ? basicFilter : "sales";

    return (
        <div className={styles.OffersSection}>
            <div className={styles.FilterSection}>
                <div className={styles.FilterTitle} id={titleId}>
                    <p onClick={onOpenTimeModal}>
                        <Trans i18nKey="home.filters.title" components={{ 1: <span /> }} />
                    </p>
                </div>
                <div className={styles.FilterContent}>
                    {filterCards.map((card) => {
                        const isSelected = !card.isSettings && basicFilter === card.filterKey;
                        const onClick = card.isSettings ? () => onOpenFilter() : handleBasic;

                        return (
                            <FilterCard
                                key={card.id}
                                text={card.titleKey ? t(card.titleKey) : undefined}
                                customIcon={card.icon}
                                filterKey={card.filterKey}
                                onClick={onClick}
                                selected={isSelected}
                                filled={card.filled}
                                
                            />
                        );
                    })}
                </div>

                <div className={styles.AdditionalFilters}>
                    {formatSelectedFilters(filterData, t).map(({ tagCategory, tagSlug, tag }) => (
                        <div className={styles.AdditionalFilter} key={`${tagCategory}-${tag}`}>
                            <p>{tag}</p>
                            <X
                                onClick={() => {
                                    const tagCat = filterData[tagCategory as keyof FilterDataProps];
                                    if (tagCat) {
                                        const newTagCat = { ...tagCat, [tagSlug]: false };
                                        handleFilter(tagCategory as keyof FilterDataProps, newTagCat);
                                    }
                                }}
                            />
                        </div>
                    ))}
                    {filterData.cena &&
                        filterData.cena.length === 2 &&
                        !(filterData.cena[0] === 0 && filterData.cena[1] === 100000) && (
                            <div className={styles.AdditionalFilter}>
                                <p>{formatPriceRange(filterData.cena[0], filterData.cena[1])}</p>
                                <X onClick={() => handleFilter("cena", [0, 100000])} />
                            </div>
                        )}
                </div>
            </div>
            {(basicFilter ?? "") == "" && deeplyEqual(filterData, defaultFilters) ? (
                <>
                    <ProductSectionComponent
                        title={t("home.products.titles.new")}
                        products={newProductItems}
                        type="new"
                        viewStyle={productLayout}
                        showButtons={true}
                        setViewStyle={setProductLayout}
                        handleFilterSelect={handleBasic}
                        limit={4}
                    />
                    <ProductSectionComponent
                        title={t("home.products.titles.products")}
                        products={[...packageItems, ...productItems, ...extraItems]}
                        type="sales"
                        viewStyle={productLayout}
                        showButtons={showSalesButtons}
                        setViewStyle={setProductLayout}
                        handleFilterSelect={handleBasic}
                        limit={4}
                        loading={loading}
                    />
                    <ProductSectionComponent
                        title={t("home.products.titles.activities")}
                        products={activityItems}
                        type="activities"
                        viewStyle={productLayout}
                        showButtons={false}
                        setViewStyle={setProductLayout}
                        handleFilterSelect={handleBasic}
                        limit={4}
                        loading={loadingActivities}
                    />
                </>
            ) : (
                <ProductSectionComponent
                    title={filterTitles[selectedFilter]}
                    products={filterDict[selectedFilter]}
                    type={selectedFilter}
                    viewStyle={productLayout}
                    showButtons={true}
                    setViewStyle={setProductLayout}
                    handleFilterSelect={handleBasic}
                    single={true}
                    limit={16}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default LandingOffersSection;
